/** Rotation FDR Finder — best rotation groups by FDR over a GW range. */
import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowsClockwise, CaretDown, CaretUp, DownloadSimple, CopySimple, Play, UserCircle } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import RotationDetail from "../components/rotation/RotationDetail";
import SquadPlanner from "../components/rotation/SquadPlanner";
import { useSharedPool } from "../lib/timing-pool-store";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/rotation`;
const POSITIONS = ["GKP", "DEF", "MID", "FWD", "TEAM"];
const TAG_STYLE = {
    "Lock rotation": "bg-fa-mint text-black",
    "Short-term punt": "bg-fa-yellow text-black",
    "Wildcard bridge": "bg-fa-sky text-black",
    "Bench cover": "border border-white/30 text-white/70",
    "Trap rotation": "bg-red-400 text-black",
    "Solid": "border border-white/20 text-white/60",
};

const COLS = [
    ["value_score", "Score"], ["avg_rotation_fdr", "Avg FDR"], ["total_price", "Cena"],
    ["green_weeks", "Green"], ["playable_weeks", "Playable"], ["red", "Red flags"],
    ["longest_playable_run", "Best run"], ["home_selected", "Doma"], ["ownership_sum", "Vlastn."],
];

export default function RotationFdrFinder() {
    const [view, setView] = useState("finder");
    const [gwFrom, setGwFrom] = useState(1);
    const [gwTo, setGwTo] = useState(6);
    const [position, setPosition] = useState("DEF");
    const [size, setSize] = useState(2);
    const [startN, setStartN] = useState(1);
    const [maxTotal, setMaxTotal] = useState("9.0");
    const [maxPlayer, setMaxPlayer] = useState("");
    const [onlyAvailable, setOnlyAvailable] = useState(true);
    const [includeDoubtful, setIncludeDoubtful] = useState(false);
    const [allowSameTeam, setAllowSameTeam] = useState(false);
    const [minMinutes, setMinMinutes] = useState(900);
    const [minStarts, setMinStarts] = useState(10);
    const [teamsExclude, setTeamsExclude] = useState("");
    const [fdrMode, setFdrMode] = useState("position");
    const [maxOwnership, setMaxOwnership] = useState("");
    const [includePromoted, setIncludePromoted] = useState(false);
    const [onlyPool, setOnlyPool] = useState(false);
    const { pool } = useSharedPool();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [sortCol, setSortCol] = useState("value_score");

    const run = async () => {
        if (startN >= size) { toast.error("Počet stavěných hráčů musí být menší než velikost rotace."); return; }
        setBusy(true);
        setExpanded(null);
        try {
            const r = await axios.get(`${API}/finder`, { params: {
                gw_from: gwFrom, gw_to: gwTo, position, size, start_n: startN,
                ...(maxTotal && position !== "TEAM" && { max_total_price: maxTotal }),
                ...(maxPlayer && position !== "TEAM" && { max_player_price: maxPlayer }),
                only_available: onlyAvailable, include_doubtful: includeDoubtful,
                min_minutes: minMinutes, min_starts: minStarts,
                allow_same_team: allowSameTeam,
                fdr_mode: fdrMode,
                include_promoted: includePromoted,
                ...(onlyPool && position !== "TEAM" && pool.length > 0 && { only_ids: pool.map((p) => p.fpl_id).join(",") }),
                ...(maxOwnership && position !== "TEAM" && { max_ownership: maxOwnership }),
                ...(teamsExclude.trim() && { teams_exclude: teamsExclude }),
                limit: 40,
            } });
            setData(r.data);
            if (!r.data.rows.length) {
                if (r.data.pool_mode) toast.warning(`Žádná kombinace z tvého poolu — pool obsahuje jen ${r.data.candidates} hráčů pozice ${position}, potřebuješ aspoň ${size}.`);
                else toast.warning("Žádná kombinace neprošla filtry — zkus uvolnit cenu nebo minuty.");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Výpočet selhal");
        } finally { setBusy(false); }
    };

    const rows = useMemo(() => {
        const rr = [...(data?.rows || [])];
        const key = { red: (r) => r.red_flag_gws.length }[sortCol] || ((r) => r[sortCol]);
        const asc = sortCol === "avg_rotation_fdr" || sortCol === "total_price" || sortCol === "red";
        rr.sort((a, b) => asc ? key(a) - key(b) : key(b) - key(a));
        return rr;
    }, [data, sortCol]);

    const csv = () => {
        const head = "rank;players;teams;price;avg_fdr;green;playable;red_flags;longest_run;home;score;tag";
        const lines = rows.map((r) => [r.rank, r.players.map((p) => p.name).join("+"), r.teams.join("+"),
            r.total_price, r.avg_rotation_fdr, r.green_weeks, r.playable_weeks,
            r.red_flag_gws.map((g) => `GW${g}`).join("+") || "none",
            r.longest_playable_run, r.home_selected, r.value_score, r.tag].join(";"));
        return [head, ...lines].join("\n");
    };
    const download = () => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv()], { type: "text/csv" }));
        a.download = `rotation-fdr-gw${gwFrom}-${gwTo}-${position}.csv`;
        a.click();
        toast.success("CSV staženo");
    };
    const copy = async () => {
        try { await navigator.clipboard.writeText(csv()); toast.success("Zkopírováno do schránky"); }
        catch { toast.error("Kopírování selhalo"); }
    };

    const num = "bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[11px] w-16 focus:outline-none focus:border-fa-mint";

    return (
        <AppShell>
            <section className="max-w-7xl mx-auto px-4 py-6 space-y-4" data-testid="rotation-fdr-page">
                <header>
                    <h1 className="font-display font-black uppercase tracking-tight text-2xl flex items-center gap-2">
                        <ArrowsClockwise size={20} weight="fill" className="text-fa-mint" /> Rotation FDR Finder
                    </h1>
                    <p className="font-mono text-[10px] text-white/50 mt-1 max-w-3xl leading-relaxed">
                        Najde rotace, kde ti skupina hráčů dá <strong className="text-white/80">každé kolo aspoň jeden dobrý zápas</strong>.
                        Adjusted FDR = oficiální FDR −0.2 doma / +0.2 venku. Green = nejlepší dostupný zápas ≤2, playable ≤3, red flag ≥4.
                    </p>
                </header>

                <div className="flex gap-1" data-testid="rot-fdr-view-tabs">
                    <button type="button" onClick={() => setView("finder")} data-testid="rot-fdr-view-finder"
                            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${view === "finder" ? "bg-fa-mint text-black border-fa-mint font-bold" : "border-white/15 text-white/60"}`}>
                        <ArrowsClockwise size={11} className="inline mb-0.5 mr-1" /> Vyhledávač
                    </button>
                    <button type="button" onClick={() => setView("squad")} data-testid="rot-fdr-view-squad"
                            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${view === "squad" ? "bg-fa-mint text-black border-fa-mint font-bold" : "border-white/15 text-white/60"}`}>
                        <UserCircle size={11} className="inline mb-0.5 mr-1" /> Můj tým
                    </button>
                </div>

                {view === "squad" && <SquadPlanner />}

                {view === "finder" && (<>
                <div className="border border-white/10 bg-[#0d0d0d] p-3 flex items-end gap-3 flex-wrap" data-testid="rot-fdr-form">
                    <label className="font-mono text-[9px] uppercase text-white/50 space-y-1">
                        <div>GW od–do</div>
                        <div className="flex gap-1">
                            <input type="number" min={1} max={38} value={gwFrom} onChange={(e) => setGwFrom(+e.target.value)} className={num} data-testid="rot-fdr-gw-from" />
                            <input type="number" min={1} max={38} value={gwTo} onChange={(e) => setGwTo(+e.target.value)} className={num} data-testid="rot-fdr-gw-to" />
                        </div>
                    </label>
                    <label className="font-mono text-[9px] uppercase text-white/50 space-y-1">
                        <div>Pozice</div>
                        <div className="flex gap-1">
                            {POSITIONS.map((p) => (
                                <button key={p} type="button" onClick={() => setPosition(p)} data-testid={`rot-fdr-pos-${p}`}
                                        className={`px-2 py-1.5 font-mono text-[10px] border ${position === p ? "bg-fa-mint text-black border-fa-mint font-bold" : "border-white/15 text-white/60"}`}>
                                    {p === "TEAM" ? "Týmy" : p}
                                </button>
                            ))}
                        </div>
                    </label>
                    <label className="font-mono text-[9px] uppercase text-white/50 space-y-1">
                        <div>Rotace / staví se</div>
                        <div className="flex gap-1 items-center">
                            <select value={size} onChange={(e) => { setSize(+e.target.value); setStartN(Math.min(startN, +e.target.value - 1)); }}
                                    className={num} data-testid="rot-fdr-size">
                                {[2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <span className="text-white/40">→</span>
                            <select value={startN} onChange={(e) => setStartN(+e.target.value)} className={num} data-testid="rot-fdr-start-n">
                                {[1, 2, 3, 4].filter((n) => n < size).map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </div>
                    </label>
                    {position !== "TEAM" && (
                        <>
                            <label className="font-mono text-[9px] uppercase text-white/50 space-y-1">
                                <div>Max cena celkem</div>
                                <input type="number" step="0.5" value={maxTotal} onChange={(e) => setMaxTotal(e.target.value)} placeholder="—" className={num} data-testid="rot-fdr-max-total" />
                            </label>
                            <label className="font-mono text-[9px] uppercase text-white/50 space-y-1">
                                <div>Max cena hráče</div>
                                <input type="number" step="0.1" value={maxPlayer} onChange={(e) => setMaxPlayer(e.target.value)} placeholder="—" className={num} />
                            </label>
                            <label className="font-mono text-[9px] uppercase text-white/50 space-y-1">
                                <div>Min minut / startů</div>
                                <div className="flex gap-1">
                                    <input type="number" step="100" value={minMinutes} onChange={(e) => setMinMinutes(+e.target.value)} className={num} />
                                    <input type="number" value={minStarts} onChange={(e) => setMinStarts(+e.target.value)} className={num} />
                                </div>
                            </label>
                            <label className="font-mono text-[9px] uppercase text-white/50 space-y-1">
                                <div>Max vlastnictví %</div>
                                <input type="number" step="1" value={maxOwnership} onChange={(e) => setMaxOwnership(e.target.value)} placeholder="—" className={num} data-testid="rot-fdr-max-ownership" />
                            </label>
                        </>
                    )}
                    <label className="font-mono text-[9px] uppercase text-white/50 space-y-1">
                        <div>FDR režim</div>
                        <div className="flex gap-1">
                            <button type="button" onClick={() => setFdrMode("position")} data-testid="rot-fdr-mode-position"
                                    title="GK/DEF hodnotí sílu útoku soupeře (clean sheet potenciál), MID/FWD sílu obrany soupeře (attacking returns). Zahrnuje H/A síly a formu soupeře."
                                    className={`px-2 py-1.5 font-mono text-[10px] border ${fdrMode === "position" ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60"}`}>
                                Dle pozice
                            </button>
                            <button type="button" onClick={() => setFdrMode("official")} data-testid="rot-fdr-mode-official"
                                    title="Oficiální FPL FDR ±0.2 dle domácí/venkovní"
                                    className={`px-2 py-1.5 font-mono text-[10px] border ${fdrMode === "official" ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60"}`}>
                                Oficiální
                            </button>
                        </div>
                    </label>
                    <label className="font-mono text-[9px] uppercase text-white/50 space-y-1">
                        <div>Vyloučit týmy</div>
                        <input type="text" value={teamsExclude} onChange={(e) => setTeamsExclude(e.target.value)} placeholder="ARS, LIV…"
                               className="bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[11px] w-28 focus:outline-none focus:border-fa-mint placeholder:text-white/25" />
                    </label>
                    <div className="flex flex-col gap-1 font-mono text-[10px] text-white/70">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                            <input type="checkbox" checked={onlyAvailable} onChange={(e) => setOnlyAvailable(e.target.checked)} className="accent-[#9EE8B4]" />
                            Jen dostupní
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                            <input type="checkbox" checked={includeDoubtful} onChange={(e) => setIncludeDoubtful(e.target.checked)} className="accent-[#9EE8B4]" />
                            Včetně nejistých
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                            <input type="checkbox" checked={allowSameTeam} onChange={(e) => setAllowSameTeam(e.target.checked)} className="accent-[#9EE8B4]" />
                            Povolit stejný tým
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer" title="Hráči nováčků bez FPL historie (odhadovaná cena 4.5, bez minut/PPG) — defaultně vyloučeni, protože zkreslují výsledky"
                               data-testid="rot-fdr-include-promoted">
                            <input type="checkbox" checked={includePromoted} onChange={(e) => setIncludePromoted(e.target.checked)} className="accent-[#9EE8B4]" />
                            Vč. nováčků (bez dat)
                        </label>
                        {position !== "TEAM" && (
                            <label className={`flex items-center gap-1.5 ${pool.length === 0 ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                                   title={pool.length === 0
                                       ? "Pool je prázdný — přidej hráče v Timing Analyzeru → Team Season DNA"
                                       : "Hledá rotace jen mezi hráči z tvého poolu (Pool Analyzer). Filtry minut/startů/ceny hráče se ignorují."}
                                   data-testid="rot-fdr-only-pool">
                                <input type="checkbox" checked={onlyPool && pool.length > 0} disabled={pool.length === 0}
                                       onChange={(e) => setOnlyPool(e.target.checked)} className="accent-[#9EE8B4]" />
                                <span className={onlyPool && pool.length > 0 ? "text-fa-mint" : ""}>Jen z mého poolu ({pool.length})</span>
                            </label>
                        )}
                    </div>
                    <button type="button" onClick={run} disabled={busy} data-testid="rot-fdr-run"
                            className="font-mono text-[10px] uppercase tracking-widest px-4 py-2 bg-fa-yellow text-black font-bold hover:bg-fa-yellowHover disabled:opacity-40 ml-auto">
                        <Play size={11} weight="fill" className="inline mb-0.5 mr-1" /> {busy ? "Počítám…" : "Najít rotace"}
                    </button>
                </div>

                {data && rows.length > 0 && (
                    <>
                        <div className="flex items-center gap-2">
                            <span className="font-mono text-[9px] text-white/40">
                                {rows.length} kombinací z {data.candidates} kandidátů · GW{data.gw_from}–{data.gw_to} · staví se {data.start_n} z {data.size}
                                {data.pool_mode && <span className="text-fa-mint font-bold"> · jen můj pool</span>}
                                {data.xg_teams > 0 && <span className="text-fa-mint/70"> · xG data: SportMonks ({data.xg_teams} týmů)</span>}
                            </span>
                            <button type="button" onClick={download} data-testid="rot-fdr-csv"
                                    className="ml-auto font-mono text-[9px] uppercase tracking-widest px-2 py-1 border border-white/20 text-white/60 hover:text-white">
                                <DownloadSimple size={10} className="inline mb-0.5 mr-1" />CSV
                            </button>
                            <button type="button" onClick={copy}
                                    className="font-mono text-[9px] uppercase tracking-widest px-2 py-1 border border-white/20 text-white/60 hover:text-white">
                                <CopySimple size={10} className="inline mb-0.5 mr-1" />Kopírovat
                            </button>
                        </div>
                        <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto">
                            <table className="w-full text-left" data-testid="rot-fdr-table">
                                <thead>
                                    <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-[0.15em] text-white/40">
                                        <th className="px-2 py-2">#</th>
                                        <th className="px-2 py-2">Hráči</th>
                                        {COLS.map(([k, label]) => (
                                            <th key={k} onClick={() => setSortCol(k)} data-testid={`rot-fdr-col-${k}`}
                                                className={`px-2 py-2 text-right cursor-pointer select-none hover:text-white ${sortCol === k ? "text-fa-mint" : ""}`}>
                                                {label}{sortCol === k ? " ▼" : ""}
                                            </th>
                                        ))}
                                        <th className="px-2 py-2">Tag</th>
                                        <th className="px-2 py-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <React.Fragment key={r.rank}>
                                            <tr className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer"
                                                data-testid={`rot-fdr-row-${r.rank}`}
                                                onClick={() => setExpanded(expanded === r.rank ? null : r.rank)}>
                                                <td className="px-2 py-2 font-mono text-[10px] text-white/40 tabular-nums">{r.rank}</td>
                                                <td className="px-2 py-2">
                                                    <span className="font-display font-bold text-sm">{r.players.map((p) => p.name).join(" + ")}</span>
                                                    <span className="font-mono text-[8px] uppercase text-white/40 ml-2">{r.teams.join(" · ")}</span>
                                                </td>
                                                <td className="px-2 py-2 text-right font-mono text-[11px] font-bold text-fa-mint tabular-nums">{r.value_score}</td>
                                                <td className="px-2 py-2 text-right font-mono text-[11px] tabular-nums">{r.avg_rotation_fdr}</td>
                                                <td className="px-2 py-2 text-right font-mono text-[11px] tabular-nums">{position === "TEAM" ? "—" : `£${r.total_price}`}</td>
                                                <td className="px-2 py-2 text-right font-mono text-[11px] text-fa-mint tabular-nums">{r.green_weeks}/{r.n_gws}</td>
                                                <td className="px-2 py-2 text-right font-mono text-[11px] text-fa-yellow tabular-nums">{r.playable_weeks}/{r.n_gws}</td>
                                                <td className="px-2 py-2 text-right font-mono text-[10px] text-red-300">
                                                    {r.red_flag_gws.length ? r.red_flag_gws.map((g) => `GW${g}`).join(", ") : "—"}
                                                </td>
                                                <td className="px-2 py-2 text-right font-mono text-[11px] tabular-nums">{r.longest_playable_run}</td>
                                                <td className="px-2 py-2 text-right font-mono text-[11px] tabular-nums text-white/60">{r.home_selected}</td>
                                                <td className="px-2 py-2 text-right font-mono text-[10px] tabular-nums text-white/60">
                                                    {position === "TEAM" ? "—" : `${r.ownership_sum}%`}
                                                </td>
                                                <td className="px-2 py-2">
                                                    <span className={`font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 whitespace-nowrap ${TAG_STYLE[r.tag] || TAG_STYLE.Solid}`}>{r.tag}</span>
                                                </td>
                                                <td className="px-2 py-2 text-fa-sky">{expanded === r.rank ? <CaretUp size={12} /> : <CaretDown size={12} />}</td>
                                            </tr>
                                            {expanded === r.rank && (
                                                <tr className="border-b border-white/5 bg-black/40">
                                                    <td colSpan={13}><RotationDetail row={r} showCs={position === "GKP" || position === "DEF"} /></td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
                {data && rows.length === 0 && (
                    <div className="border border-white/10 bg-[#0d0d0d] p-6 font-mono text-[11px] text-white/50 text-center" data-testid="rot-fdr-empty">
                        Žádná kombinace neprošla filtry.
                    </div>
                )}
                </>)}
            </section>
        </AppShell>
    );
}
